import { useState, useCallback, useEffect, useRef } from "react";
import { getDocumentByFilename } from "@/stores/documents/repository";
import { loadDocumentPreview } from "@/stores/documents/repository/preview";
import { isDwgParentDoc, useDrawingViewer } from "@/domains/documents/hooks/useDrawingViewer";
import { usePermissions } from "@/hooks/usePermissions";
import { canPreviewFile } from "@/utils/previewUtils";
import type { Document } from "@/types/documents";

interface DocumentPreviewState {
  /** Whether the viewer dialog is open */
  isOpen: boolean;
  /** The document being previewed */
  document: Document | null;
  /** Pre-loaded blob URL for the document */
  blobUrl: string | null;
  /** Raw blob data for Office file rendering */
  blobData: Blob | null;
  /** Original DWG file path (for DWG files) */
  originalDwgFilePath: string | null;
  /** Whether a document is currently loading */
  isLoading: boolean;
  /** Error message if loading failed */
  error: string | null;
}

/** Minimal doc shape accepted by `openPreviewByDoc`. Matches `DocumentLike` in
 * `stores/documents/repository/preview.ts`. */
export interface PreviewableDoc {
  id: string | number;
  filename: string;
  file_path: string;
  building_id: string | number;
  content_type: string | null;
  size: number | null;
  created_at: string;
  parent_document_id?: string | number | null;
  version?: number;
  created_by_name?: string;
}

interface UseDocumentPreviewResult extends DocumentPreviewState {
  /** Open preview for a document by filename (looks it up by name first). */
  openPreview: (filename: string) => Promise<void>;
  /** Open preview for a doc whose row is already in hand (no name lookup).
   *  Use this when the caller has the doc id + file_path already — e.g. the
   *  chatbot Files panel — to avoid the filename collision risk in
   *  `getDocumentByFilename`. */
  openPreviewByDoc: (doc: PreviewableDoc) => Promise<void>;
  /** Close the preview dialog */
  closePreview: () => void;
  /** Clear any error state */
  clearError: () => void;
}

/**
 * Hook to manage document preview state for chatbot document links.
 * Looks up documents by filename, then delegates to the shared
 * loadDocumentPreview() — the same code path the document list uses.
 *
 * `buildingId` scopes the filename lookup `openPreview` does; a caller that
 * only ever opens documents it already holds a row for (the signature panel)
 * omits it and uses `openPreviewByDoc`.
 *
 * A parent `.dwg` never reaches `loadDocumentPreview`: its DWG branch resolves
 * the converted child PDF, which this branch stopped treating as a preview
 * rendition, so it dead-ends in "child PDF not found". Every caller of this
 * hook — the chatbot doc links and Files panel, the building header's source
 * chips, the Building data page's source links and the signature panel's
 * enclosed documents — routes a DWG to the native Drawings viewer instead. The
 * guard lives here rather than in each of those five so they cannot drift.
 */
export function useDocumentPreview(buildingId = ""): UseDocumentPreviewResult {
  const [state, setState] = useState<DocumentPreviewState>({
    isOpen: false,
    document: null,
    blobUrl: null,
    blobData: null,
    originalDwgFilePath: null,
    isLoading: false,
    error: null,
  });

  // Used to ensure "latest request wins" when openPreview is called rapidly.
  const requestIdRef = useRef(0);

  const { canViewDrawingCompareTool } = usePermissions();
  const { openDrawingViewer } = useDrawingViewer([], undefined, {
    enabled: canViewDrawingCompareTool,
    buildingId: buildingId || undefined,
  });

  /** Claims a parent `.dwg` before it can reach the preview loader. Returns
   *  true when it handled the document, so both openers just bail. Without the
   *  drawing-tool grant there is nothing to open, and saying so beats a click
   *  that silently does nothing — all five callers already toast `error`. */
  const routeDrawing = useCallback(
    (doc: { id: string | number; filename: string }) => {
      if (!isDwgParentDoc(doc)) return false;
      // `openPreview` has already flipped isLoading on by the time it can know
      // the row is a DWG, so both branches clear it — navigation away is not
      // guaranteed (the project lookup can be superseded) and a spinner nobody
      // resolves is worse than the click appearing to do nothing.
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: canViewDrawingCompareTool
          ? null
          : `"${doc.filename}" opens in the drawing viewer, which you don't have access to`,
      }));
      if (canViewDrawingCompareTool) openDrawingViewer([doc.id]);
      return true;
    },
    [canViewDrawingCompareTool, openDrawingViewer]
  );

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (state.blobUrl) {
        URL.revokeObjectURL(state.blobUrl);
      }
    };
  }, [state.blobUrl]);

  // Invalidate any in-flight requests on unmount
  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
    };
  }, []);

  const openPreview = useCallback(
    async (filename: string) => {
      if (!canPreviewFile(filename)) return;

      const requestId = (requestIdRef.current += 1);

      // Clean up any existing blob URL
      setState((prev) => {
        if (prev.blobUrl) {
          URL.revokeObjectURL(prev.blobUrl);
        }
        return { ...prev, isLoading: true, error: null, blobUrl: null, blobData: null };
      });

      try {
        // Step 1: Look up the document by filename
        console.log("[ChatPreview] Looking up:", { buildingId, filename });
        const doc = await getDocumentByFilename(buildingId, filename);
        if (requestId !== requestIdRef.current) return;

        if (!doc) {
          console.log("[ChatPreview] Document not found for filename:", filename);
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: `Document "${filename}" not found`,
          }));
          return;
        }

        console.log("[ChatPreview] Found doc:", {
          id: doc.id,
          filename: doc.filename,
          file_path: doc.file_path,
          content_type: doc.content_type,
        });

        // Step 2: a DWG goes to the Drawings viewer, never to the preview
        // loader's child-PDF branch. Needs the row's id, so it waits on the
        // lookup above.
        if (routeDrawing(doc)) return;

        // Step 3: Use the SAME preview loader as the document list
        // Handles blob download and content type
        const result = await loadDocumentPreview(doc);
        if (requestId !== requestIdRef.current) {
          if (result.blobUrl) URL.revokeObjectURL(result.blobUrl);
          return;
        }

        console.log("[ChatPreview] loadDocumentPreview result:", {
          docFilename: result.document.filename,
          docContentType: result.document.content_type,
          blobUrl: result.blobUrl?.slice(0, 30),
          blobSize: result.blobData?.size,
          blobType: result.blobData?.type,
          originalDwgFilePath: result.originalDwgFilePath,
        });

        setState({
          isOpen: true,
          document: result.document,
          blobUrl: result.blobUrl,
          blobData: result.blobData,
          originalDwgFilePath: result.originalDwgFilePath,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        console.error("[ChatPreview] Error:", error);
        const message = error instanceof Error ? error.message : "Failed to load document";
        setState((prev) => ({ ...prev, isLoading: false, error: message }));
      }
    },
    [buildingId, routeDrawing]
  );

  const openPreviewByDoc = useCallback(
    async (doc: PreviewableDoc) => {
      if (!canPreviewFile(doc.filename, doc.content_type)) return;
      if (routeDrawing(doc)) return;

      const requestId = (requestIdRef.current += 1);

      setState((prev) => {
        if (prev.blobUrl) {
          URL.revokeObjectURL(prev.blobUrl);
        }
        return { ...prev, isLoading: true, error: null, blobUrl: null, blobData: null };
      });

      try {
        const result = await loadDocumentPreview(doc);
        if (requestId !== requestIdRef.current) {
          if (result.blobUrl) URL.revokeObjectURL(result.blobUrl);
          return;
        }

        setState({
          isOpen: true,
          document: result.document,
          blobUrl: result.blobUrl,
          blobData: result.blobData,
          originalDwgFilePath: result.originalDwgFilePath,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        console.error("[ChatPreview] openPreviewByDoc error:", error);
        const message = error instanceof Error ? error.message : "Failed to load document";
        setState((prev) => ({ ...prev, isLoading: false, error: message }));
      }
    },
    [routeDrawing]
  );

  const closePreview = useCallback(() => {
    requestIdRef.current += 1;
    setState((prev) => {
      if (prev.blobUrl) {
        URL.revokeObjectURL(prev.blobUrl);
      }
      return {
        isOpen: false,
        document: null,
        blobUrl: null,
        blobData: null,
        originalDwgFilePath: null,
        isLoading: false,
        error: null,
      };
    });
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return { ...state, openPreview, openPreviewByDoc, closePreview, clearError };
}
